import { useState } from 'react';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { Button, LoadingIndicator, Pagination } from 'components/common';
import { FormGroup } from 'components/common/form';

interface Props {
  baseMaxHeight?: number;
  items: React.ReactElement<any>[];
  loading?: boolean;
  maxColumnsSmallWidth?: number;
  maxColumnsLargeWidth?: number;
  style?: React.CSSProperties;
  title?: string;
}

const ContainerWithShowAll = ({
  baseMaxHeight = 50,
  items,
  loading = false,
  maxColumnsSmallWidth,
  maxColumnsLargeWidth,
  style,
  title
}: Props) => {
  const { messages, theme } = useReduxState();

  const [showAll, setShowAll] = useState(false);
  const [page, setPage] = useState(1);

  const itemsPerPage = 100;
  const numberOfItems = items?.length || 0;
  const numberOfPages = Math.ceil((items?.length || 0) / itemsPerPage) || 1;
  const selectedIndex = (page - 1) * itemsPerPage;
  const selectedItems = items?.slice(
    selectedIndex,
    selectedIndex + itemsPerPage
  );

  const handleShowAll: React.MouseEventHandler<HTMLButtonElement> = (ev) => {
    ev.stopPropagation();
    setShowAll(!showAll);
    if (!showAll) setPage(1);
  };

  const isExpandable = numberOfItems > (maxColumnsLargeWidth || 4);

  return loading ? (
    <LoadingIndicator />
  ) : (
    <FormGroup label={title ? title : ''} maxWidth="100%" style={style}>
      <div
        className={
          `expandable-container` +
          `${showAll || !isExpandable ? '' : ' max-height'}`
        }
      >
        <div className="items">{selectedItems}</div>

        {numberOfPages > 1 ? (
          <div className="pagination">
            <Pagination
              currentPage={page}
              numberOfPages={numberOfPages}
              onPageChange={({ page }) => setPage(page)}
            />
          </div>
        ) : null}
      </div>

      {isExpandable ? (
        <div style={{ textAlign: 'center' }}>
          <Button
            className="show-all"
            category="light"
            icon={showAll ? 'chevron-up' : 'chevron-down'}
            iconStyle={{
              color: theme.textColorLight,
              fontSize: '1rem',
              marginLeft: 5,
              position: 'relative',
              top: -1
            }}
            onClick={handleShowAll}
            style={{
              borderRadius: '100px',
              marginTop: 10,
              padding: '5px 15px 5px 5px'
            }}
            text={showAll ? messages.loginHide : messages.loginShowAll}
          />
        </div>
      ) : null}

      <style jsx>{`
        .expandable-container {
          overflow-y: hidden;
          padding: 5px 10px;
          width: 100%;
        }
        .expandable-container.max-height {
          max-height: ${baseMaxHeight * 4}px;
        }
        .items {
          display: grid;
          gap: 20px 10px;
          transition: grid-template-rows 5s ease-in-out;
        }
        @media (min-width: ${breakpoints.small}px) {
          .expandable-container.max-height {
            max-height: ${baseMaxHeight * 2}px;
          }
          .items {
            grid-template-columns: ${maxColumnsSmallWidth
              ? `repeat(
                ${
                  numberOfItems > maxColumnsSmallWidth
                    ? maxColumnsSmallWidth
                    : numberOfItems
                }, 
                50%
              )`
              : 'repeat(auto-fit, minmax(200px, 1fr))'};
          }
        }
        @media (min-width: ${breakpoints.large}px) {
          .expandable-container.max-height {
            max-height: ${baseMaxHeight}px;
          }
          .items {
            grid-template-columns: ${maxColumnsLargeWidth
              ? `repeat(
                ${
                  numberOfItems > maxColumnsLargeWidth
                    ? maxColumnsLargeWidth
                    : numberOfItems
                }, 
                1fr
              )`
              : 'repeat(auto-fit, minmax(200px, 1fr))'};
          }
        }
      `}</style>
    </FormGroup>
  );
};

export default ContainerWithShowAll;
